// Imports
import JoditEditor from "jodit-react";
import { FunctionComponent, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { joditConfig } from "v2/constants/constants";
import { cleanEmptyHtml } from "common/helpers/joditHelpers";

// Main component properties types
type PropertyDescriptionSectionProps = {
  data: {
    highlights: string;
    overview: string;
    brokerCommission?: number | null;
    setHighlights: (value: string) => void;
    setOverview: (value: string) => void;
    setBrokerCommission: (value: number | null) => void;
  };
};

// Main Component
export const PropertyDescriptionSection: FunctionComponent<
  PropertyDescriptionSectionProps
> = (props) => {
  const { t } = useTranslation(["common"]);

  // Set HTML tags references
  const highlightEditor = useRef(null);
  const overviewEditor = useRef(null);

  // Jodit configuration (Any type to avoid Jodit's bug)
  const config: any = useMemo(() => joditConfig, [props]);

  // Handler for broker commission input changes
  const handleBrokerCommissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      props.data.setBrokerCommission(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 99) {
        // Round to 1 decimal place
        props.data.setBrokerCommission(Math.round(numValue * 10) / 10);
      }
    }
  };

  // Memoized Jodit editors (must have empty deps to avoid Jodit's bug)
  const joditEditors = useMemo(
    () => (
      <>
        <div>
          <label className="text-sm text-slate-600">
            {t("common:labels.agentHighlightsNotes")}
          </label>

          <div>
            <JoditEditor
              ref={highlightEditor}
              value={props.data.highlights}
              key="highlight-editor"
              onChange={(value) => {
                const cleanedValue = cleanEmptyHtml(value);
                props.data.setHighlights(cleanedValue);
              }}
              config={{
                ...config,
                placeholder:
                  "Add any highlights related to the property. Max of 4000 characters.",
              }}
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-slate-600">
            {t("common:labels.propertyOverview")}
          </label>

          <div>
            <JoditEditor
              ref={overviewEditor}
              value={props.data.overview}
              key="overview-editor"
              onChange={(value) => {
                const cleanedValue = cleanEmptyHtml(value);
                props.data.setOverview(cleanedValue);
              }}
              config={{
                ...config,
                placeholder:
                  "Add the property's project overiew. Max of 4000 characters.",
              }}
            />
          </div>
        </div>
      </>
    ),
    []
  );

  // Return HTML component with broker commission outside of memoization
  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div>
            <label className="text-sm text-slate-600">
              {t("common:labels.brokerCommissionPercent")}
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="99"
              value={
                props.data.brokerCommission !== null &&
                props.data.brokerCommission !== undefined
                  ? String(props.data.brokerCommission)
                  : ""
              }
              onChange={handleBrokerCommissionChange}
              placeholder="e.g., 6.5"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          {joditEditors}
        </div>
      </div>
    </div>
  );
};
